import { useMemo } from 'react'

import { useSearchParams } from '../../../hooks/useSearchParams'
import { LiveEventGroupBy } from '../utils/liveEventExamplesGrouping'

export type LiveEventExamplesSearchParamSource = {
  gameIds: string | string[]
  liveEventTags: string[]
  liveEventSecondaryTags: string[]
  motivations: string[]
  archetypes: string[]
  liveEventAppearances: string[]
  dateFrom?: string
  dateTo?: string
  groupBy?: string
  hideDiscontinuedBlueprints?: string
}

type SearchParamDestination = {
  gameIds: string[]
  liveEventTags: string[]
  liveEventSecondaryTags: string[]
  motivations: string[]
  archetypes: string[]
  liveEventAppearances: string[]
  dateFrom?: number
  dateTo?: number
  groupBy: LiveEventGroupBy
  hideDiscontinuedBlueprints: boolean
}

type LiveEventExamplesSearchParamsOptions = {
  hasAccessToBlueprints?: boolean
}

export const useLiveEventExamplesSearchParams = (options?: LiveEventExamplesSearchParamsOptions) => {
  const hasAccessToBlueprints = options?.hasAccessToBlueprints ?? false

  const mapper = useMemo(
    () => ({
      mapFromUrl: (source: LiveEventExamplesSearchParamSource): SearchParamDestination => {
        return {
          gameIds: Array.isArray(source.gameIds) ? source.gameIds : source.gameIds ? [source.gameIds] : [],
          liveEventTags: source.liveEventTags || [],
          liveEventSecondaryTags: source.liveEventSecondaryTags || [],
          motivations: source.motivations || [],
          archetypes: source.archetypes || [],
          liveEventAppearances: source.liveEventAppearances || [],
          dateFrom: source.dateFrom ? Number(source.dateFrom) : undefined,
          dateTo: source.dateTo ? Number(source.dateTo) : undefined,
          groupBy: hasAccessToBlueprints && source.groupBy === LiveEventGroupBy.Blueprint ? LiveEventGroupBy.Blueprint : LiveEventGroupBy.Type,
          hideDiscontinuedBlueprints: source.hideDiscontinuedBlueprints === 'false' ? false : true,
        }
      },
      mapToUrl: (destination: SearchParamDestination): LiveEventExamplesSearchParamSource => {
        return {
          gameIds: destination.gameIds || [],
          liveEventTags: destination.liveEventTags || [],
          liveEventSecondaryTags: destination.liveEventSecondaryTags || [],
          motivations: destination.motivations || [],
          archetypes: destination.archetypes || [],
          liveEventAppearances: destination.liveEventAppearances || [],
          dateFrom: destination.dateFrom != null ? String(destination.dateFrom) : undefined,
          dateTo: destination.dateTo != null ? String(destination.dateTo) : undefined,
          groupBy: destination.groupBy,
          hideDiscontinuedBlueprints: String(destination.hideDiscontinuedBlueprints),
        }
      },
    }),
    [hasAccessToBlueprints]
  )

  return useSearchParams<LiveEventExamplesSearchParamSource, SearchParamDestination>(mapper)
}
